// ActivitySystemEvent.jsx

import React, { useMemo } from 'react';

// Utilities
import { formatDateTime } from 'utilities/DateTime';
import {
  formatActivitySystemMessages,
  getActivityActorName,
  resolveStoryDigestPreviews,
} from 'node/activity/utils/formatActivitySystemMessages';
import { toActivityTimestampMs } from 'node/activity/utils/buildActivityFeedItems';

// CSS
import './ActivitySystemEvent.css';

// Components
import ActivityReactionControl from 'node/activity/items/ActivityReactionControl';
import ActivityRelationshipChip, {
  resolveRelatedNodeFromActivityEvent,
} from 'node/activity/items/ActivityRelationshipChip';

/**
 * ActivitySystemEvent
 *
 * Quiet inline system-change line for the Activity feed.
 *
 * @param {object} event - Node activity event.
 * @param {Record<string, string>} fieldLabels - Field path → label lookup.
 * @param {Record<string, object>} [fieldDefs] - Field path → schema field def.
 * @param {string} [typeLabel] - Node type label for create / placeholder filters.
 * @param {'datetime'|'time'} [timestampStyle] - Full date+time, or time only.
 * @param {object|null} [node] - Host or TASK node this event belongs to.
 * @param {object[]} [reactions] - ACTIVITY_REACTION rows for this event.
 * @returns {ReactNode}
 */
const ActivitySystemEvent = React.memo(({
  event,
  fieldLabels = {},
  fieldDefs = {},
  typeLabel = '',
  timestampStyle = 'datetime',
  node = null,
  reactions = [],
}) => {
  const actorName = getActivityActorName(event);
  const messages = useMemo(
    () => formatActivitySystemMessages({ event, fieldLabels, fieldDefs, typeLabel }),
    [event, fieldDefs, fieldLabels, typeLabel]
  );

  const related = useMemo(
    () => resolveRelatedNodeFromActivityEvent(event),
    [event]
  );

  const isStoryBeat = event?.kind === 'story_beat'
    || event?.type === 'node_story_beat';
  const isDigest = isStoryBeat && String(event?.storySubtype || '').trim() === 'digest';
  const digestPreviews = useMemo(
    () => (isDigest ? resolveStoryDigestPreviews(event) : []),
    [event, isDigest]
  );
  const digestCount = Number(event?.digestCount);
  const digestRemaining = isDigest
    && Number.isFinite(digestCount)
    && digestCount > digestPreviews.length
    ? digestCount - digestPreviews.length
    : 0;

  const isRelationship = event?.kind === 'relationship-changed'
    || event?.type === 'node_relationship_changed'
    || (Boolean(related) && !isStoryBeat);
  const showRelatedChip = Boolean(related) && (isRelationship || isStoryBeat) && !isDigest;
  const showDigestChips = isDigest && digestPreviews.length > 0;

  const occurredMs = toActivityTimestampMs(event?.occurredAt)
    || toActivityTimestampMs(event?.timestamp)
    || toActivityTimestampMs(event?.dateCreated);
  const timestampLabel = occurredMs
    ? formatDateTime(
      new Date(occurredMs),
      timestampStyle === 'time'
        ? { intlOptions: { hour: 'numeric', minute: '2-digit', hour12: true } }
        : undefined
    )
    : '';

  if (!messages.length) return null;

  return (
    <div className="activity-system-event-container">
      <div className="activity-system-event-row">
        <div className="activity-system-event-main">
          {messages.map((message, index) => (
            <div
              key={`${index}:${message}`}
              className="activity-system-event-body"
            >
              <span className="activity-system-event-bullet" aria-hidden="true">•</span>
              <span className="activity-system-event-copy">
                <span className="activity-system-event-actor">{actorName}</span>
                <span className="activity-system-event-message">{message}</span>
              </span>
            </div>
          ))}
          {showRelatedChip ? (
            <div className="activity-system-event-related">
              <ActivityRelationshipChip event={event} />
            </div>
          ) : null}
          {showDigestChips ? (
            <div className="activity-system-event-related activity-system-event-digest-chips">
              {digestPreviews.map((preview) => (
                <ActivityRelationshipChip
                  key={preview.key}
                  event={{
                    relatedNode: {
                      key: preview.key,
                      title: preview.title,
                      type: preview.type,
                      typeLabel: preview.typeLabel,
                    },
                  }}
                />
              ))}
              {digestRemaining > 0 ? (
                <div className="activity-system-event-digest-more">
                  {digestRemaining === 1
                    ? '+1 more in Related'
                    : `+${digestRemaining} more in Related`}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="activity-system-event-meta">
          <ActivityReactionControl
            event={event}
            node={node}
            reactions={reactions}
          />
          {timestampLabel ? (
            <div className="activity-system-event-time">{timestampLabel}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
});

ActivitySystemEvent.displayName = 'ActivitySystemEvent';

export default ActivitySystemEvent;
